'use client';

import { Country } from '@/i18n/i18nConfig.types';
import { useOfferToolUser } from '@/providers/OfferToolUserProvider/OfferToolUserProvider';
import { createContext, ReactNode, useContext } from 'react';

const CountryContext = createContext<{
  getCountry: () => Country;
}>({
  getCountry: () => {
    throw new Error('useCountry must be used within a CountryProvider');
  },
});

/**
 * @deprecated CountryProvider is deprecated. Use OfferToolUserProvider directly via useOfferToolUser().getCountry()
 *
 * This provider is a pass-through wrapper that reads from OfferToolUserProvider.
 * It does NOT make any requests - it only reads from OfferToolUserProvider's context.
 * OfferToolUserProvider is the single source of truth for user data (and country).
 *
 * @param country - Optional country for testing purposes. If provided, uses this instead of reading from OfferToolUserProvider.
 */
export const CountryProvider = ({
  children,
  country: testCountry,
}: {
  children: ReactNode;
  // country prop is deprecated - country is now derived from user data via OfferToolUserProvider
  // Only used for testing purposes - allows tests to provide country without OfferToolUserProvider
  country?: Country;
}) => {
  // Read from OfferToolUserProvider (requires OfferToolUserProvider to be in the tree)
  // If testCountry is provided, use it instead (for testing without OfferToolUserProvider)
  const { getCountry: getUserCountry } = useOfferToolUser();
  const getCountry =
    testCountry !== undefined ? () => testCountry : getUserCountry;

  return (
    <CountryContext.Provider
      value={{
        getCountry,
      }}
    >
      {children}
    </CountryContext.Provider>
  );
};

/**
 * @deprecated useCountry is deprecated. Use useOfferToolUser().getCountry() instead.
 */
export const useCountry = () => {
  const context = useContext(CountryContext);
  if (context === undefined) {
    throw new Error('useCountry must be used within a CountryProvider');
  }
  return context;
};
